import { isVendedorScopedRole } from "@/types/auth";

import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FolderOpen, 
  User, 
  FileText, 
  Settings, 
  FileCheck, 
  DollarSign, 
  AlertTriangle, 
  Wrench,
  History,
  Eye,
  Plus,
  Search,
  Filter,
  Calendar,
  MapPin,
  Calculator,
  Trash2
} from "lucide-react";
import { useProjetosPaginated, useProjetosCount, type Projeto, type ProjetosSort, type ProjetosFiltroParams } from "@/hooks/useProjetos";
import { useCriarOrcamento } from "@/hooks/useOrcamentos";
import { useProjetoEquipamentos } from "@/hooks/useProjetos";
import { ProjetoDetalhes } from "./ProjetoDetalhes";
import { ExcluirProjetoDialog } from "./ExcluirProjetoDialog";
import { EditarProjetoDialog } from "./EditarProjetoDialog.tsx";
import { EditarProjetoEquipamentosDialog } from "./EditarProjetoEquipamentosDialog";
import { NovoProjeto } from "./NovoProjeto";
import { SelecionarPlanoDialog } from "./SelecionarPlanoDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useUserNamesByIds } from "@/hooks/useUserNamesByIds";
import { useUsuarios } from "@/hooks/useUsuarios";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { BRASIL_UFS } from "@/lib/estados";
import { useQueryClient } from "@tanstack/react-query";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";

export function Projetos() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [buscaProjetoInput, setBuscaProjetoInput] = useState(""); // Estado local para o input
  const [buscaProjeto, setBuscaProjeto] = useState(""); // Estado para a query (com debounce)
  const [sort, setSort] = useState<ProjetosSort>(() => ({ field: "created_at", direction: "desc" }));
  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      departamento: "todos",
      criadoPorId: "",
      estado: "",
      cidade: "",
      paineisMin: "",
      paineisMax: "",
      valorProjetoMin: "",
      valorProjetoMax: "",
      valorSeguroMin: "",
      valorSeguroMax: "",
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: usuarios = [] } = useUsuarios();
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);
  
  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (buscaProjetoInput === "") {
      setBuscaProjeto("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setBuscaProjeto(buscaProjetoInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [buscaProjetoInput]);
  
  const filtroDepartamentoHook = (filtrosAplicados.departamento || "todos").toLowerCase();
  const filtrosHook = useMemo<ProjetosFiltroParams | null>(() => {
    const toNum = (s: string) => {
      const trimmed = String(s ?? "").trim();
      if (trimmed === "") return undefined;
      const n = Number(trimmed.replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    };

    const paineisMin = toNum(filtrosAplicados.paineisMin);
    const paineisMax = toNum(filtrosAplicados.paineisMax);
    const valorProjetoMin = toNum(filtrosAplicados.valorProjetoMin);
    const valorProjetoMax = toNum(filtrosAplicados.valorProjetoMax);
    const valorSeguroMin = toNum(filtrosAplicados.valorSeguroMin);
    const valorSeguroMax = toNum(filtrosAplicados.valorSeguroMax);

    return {
      periodo: { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined },
      criadoPorId: filtrosAplicados.criadoPorId || null,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
      paineis: paineisMin != null || paineisMax != null ? { min: paineisMin, max: paineisMax } : null,
      valorProjeto: valorProjetoMin != null || valorProjetoMax != null ? { min: valorProjetoMin, max: valorProjetoMax } : null,
      valorSeguro: valorSeguroMin != null || valorSeguroMax != null ? { min: valorSeguroMin, max: valorSeguroMax } : null,
    } satisfies ProjetosFiltroParams;
  }, [filtrosAplicados]);

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setBuscaProjetoInput("");
    setBuscaProjeto("");
    setCurrentPage(1);
  };

  // Buscar projetos paginados com busca e filtros + ordenação (mesma lógica de Clientes)
  const { data: projetosResult, isLoading, error } = useProjetosPaginated(
    currentPage,
    itemsPerPage,
    buscaProjeto,
    filtroDepartamentoHook,
    sort,
    filtrosHook
  );
  const projetos = projetosResult?.data || [];
  const totalProjetos = projetosResult?.count || 0;
  const criarOrcamento = useCriarOrcamento();
  const isCreatingOrcamento = criarOrcamento.isPending;
  const { profile: currentUser, user, loading: authLoading, canDelete } = useAuth();
  const queryClient = useQueryClient();
  
  // Buscar estatísticas do dashboard usando queries diretas
  const [stats, setStats] = useState({
    orcamentosCriados: 0,
    pedidosAprovados: 0,
    contratosAtivos: 0,
    projetosNoFinanceiro: 0
  });

  const isVendedor = isVendedorScopedRole(currentUser?.role) && !!user?.id;

  const { data: userNamesMap } = useUserNamesByIds(
    projetos.map((p: any) => p.vendedor_id ?? p.orcamento?.vendedor_id)
  );

  const buscarEstatisticas = async () => {
    try {
      // 1. Contar orçamentos criados (todos os ativos; se vendedor, só os dele)
      let qOrcamentos = supabase
        .from('orcamentos')
        .select('*', { count: 'exact', head: true })
        .eq('ativo', true);
      if (isVendedor) qOrcamentos = qOrcamentos.eq('vendedor_id', user!.id);
      const { count: countOrcamentos } = await qOrcamentos;

      let countPedidos = 0;
      let countContratos = 0;
      let projetosIds = new Set<number>();

      if (isVendedor) {
        // 2. Pedidos aprovados: só os que vêm de orçamentos do vendedor
        const { data: orcamentosVendedor } = await supabase
          .from('orcamentos')
          .select('id')
          .eq('vendedor_id', user!.id);
        const orcamentoIds = (orcamentosVendedor || []).map((o: { id: number }) => o.id);
        if (orcamentoIds.length > 0) {
          const { count } = await supabase
            .from('pedidos')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'aprovado')
            .in('orcamento_id', orcamentoIds);
          countPedidos = count || 0;
        }
        // 3. Contratos ativos: só os de projetos do vendedor
        const { data: projetosVendedor } = await supabase
          .from('projetos')
          .select('id')
          .eq('vendedor_id', user!.id);
        const projetoIdsVendedor = new Set((projetosVendedor || []).map((p: { id: number }) => p.id));
        if (projetoIdsVendedor.size > 0) {
          const { count } = await supabase
            .from('contratos')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'ativo')
            .in('projeto_id', Array.from(projetoIdsVendedor));
          countContratos = count || 0;
        }
        // 4. Projetos no financeiro: transações ligadas a pedidos/contratos dos projetos do vendedor
        const { data: transacoes } = await supabase
          .from('transacoes_financeiras')
          .select('pedido_id, contrato_id')
          .or('pedido_id.not.is.null,contrato_id.not.is.null');
        const pedidosIds = new Set(transacoes?.filter(t => t.pedido_id).map(t => t.pedido_id) || []);
        const contratosIds = new Set(transacoes?.filter(t => t.contrato_id).map(t => t.contrato_id) || []);
        if (pedidosIds.size > 0) {
          const { data: pedidos } = await supabase
            .from('pedidos')
            .select('projeto_id')
            .in('id', Array.from(pedidosIds))
            .not('projeto_id', 'is', null);
          pedidos?.forEach((p: { projeto_id?: number }) => {
            if (p.projeto_id && projetoIdsVendedor.has(p.projeto_id)) projetosIds.add(p.projeto_id);
          });
        }
        if (contratosIds.size > 0) {
          const { data: contratos } = await supabase
            .from('contratos')
            .select('projeto_id')
            .in('id', Array.from(contratosIds))
            .not('projeto_id', 'is', null);
          contratos?.forEach((c: { projeto_id?: number }) => {
            if (c.projeto_id && projetoIdsVendedor.has(c.projeto_id)) projetosIds.add(c.projeto_id);
          });
        }
      } else {
        // Admin/operator/viewer/TI: totais gerais
        const { count: cPedidos } = await supabase
          .from('pedidos')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'aprovado');
        countPedidos = cPedidos || 0;
        const { count: cContratos } = await supabase
          .from('contratos')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'ativo');
        countContratos = cContratos || 0;
        const { data: transacoes, error: errorTransacoes } = await supabase
          .from('transacoes_financeiras')
          .select('pedido_id, contrato_id')
          .or('pedido_id.not.is.null,contrato_id.not.is.null');
        if (!errorTransacoes && transacoes) {
          const pIds = new Set(transacoes.filter(t => t.pedido_id).map(t => t.pedido_id));
          const cIds = new Set(transacoes.filter(t => t.contrato_id).map(t => t.contrato_id));
          if (pIds.size > 0) {
            const { data: pedidos } = await supabase
              .from('pedidos')
              .select('projeto_id')
              .in('id', Array.from(pIds))
              .not('projeto_id', 'is', null);
            pedidos?.forEach((p: { projeto_id?: number }) => {
              if (p.projeto_id) projetosIds.add(p.projeto_id);
            });
          }
          if (cIds.size > 0) {
            const { data: contratos } = await supabase
              .from('contratos')
              .select('projeto_id')
              .in('id', Array.from(cIds))
              .not('projeto_id', 'is', null);
            contratos?.forEach((c: { projeto_id?: number }) => {
              if (c.projeto_id) projetosIds.add(c.projeto_id);
            });
          }
        }
      }

      setStats({
        orcamentosCriados: countOrcamentos || 0,
        pedidosAprovados: countPedidos,
        contratosAtivos: countContratos,
        projetosNoFinanceiro: projetosIds.size
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    buscarEstatisticas();
  }, [isVendedor, user?.id, authLoading]);

  // Atualizar estatísticas quando contratos ou orçamentos mudarem
  useEffect(() => {
    const handleInvalidateStats = () => {
      // Aguardar um pouco para garantir que o banco foi atualizado
      setTimeout(() => {
        buscarEstatisticas();
      }, 500);
    };

    // Escutar eventos de criação/atualização de contratos e orçamentos
    window.addEventListener('contrato_criado', handleInvalidateStats);
    window.addEventListener('contrato_atualizado', handleInvalidateStats);
    window.addEventListener('orcamento_criado', handleInvalidateStats);

    return () => {
      window.removeEventListener('contrato_criado', handleInvalidateStats);
      window.removeEventListener('contrato_atualizado', handleInvalidateStats);
      window.removeEventListener('orcamento_criado', handleInvalidateStats);
    };
  }, []);
  const [projetoSelecionado, setProjetoSelecionado] = useState<number | null>(null);
  const [showNovoProjeto, setShowNovoProjeto] = useState(false);
  const [showGerarOrcamento, setShowGerarOrcamento] = useState(false);
  const [projetoParaEditar, setProjetoParaEditar] = useState<Projeto | null>(null);
  const [projetoEquipamentos, setProjetoEquipamentos] = useState<Projeto | null>(null);
  const [projetoParaOrcamento, setProjetoParaOrcamento] = useState<any>(null);
  const [projetoParaExcluir, setProjetoParaExcluir] = useState<number | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  // Escutar evento para abrir modal de novo projeto
  useEffect(() => {
    const handleAbrirNovoProjeto = () => {
      setShowNovoProjeto(true);
    };

    window.addEventListener('abrir_novo_projeto', handleAbrirNovoProjeto);
    
    return () => {
      window.removeEventListener('abrir_novo_projeto', handleAbrirNovoProjeto);
    };
  }, []);

  // Verificar se há um projeto específico para abrir
  useEffect(() => {
    const projetoParaAbrir = localStorage.getItem('projetoParaAbrir');
    if (projetoParaAbrir) {
      const projetoId = parseInt(projetoParaAbrir);
      setProjetoSelecionado(projetoId);
      localStorage.removeItem('projetoParaAbrir');
      
      toast.success(`Projeto ID ${projetoId} selecionado`);
    }
  }, [projetos]);

  // Resetar para página 1 quando busca ou filtro mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaProjeto, filtrosAplicados, sort.field, sort.direction]);

  const toggleSort = (field: ProjetosSort["field"]) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  };

  type ProjetoRow = Projeto & {
    cliente_nome: string;
    criado_por: string;
    departamento_label: string;
    paineis_info: string;
    valor_projeto_info: string;
    valor_seguro: string;
    criado_em: string;
    cliente_localizacao: string;
  };
  const projetoColumns: ListagemColumn<ProjetoRow>[] = [
    { key: "id", label: "ID", value: (p) => p.id },
    { key: "cliente", label: "Cliente", value: (p) => p.cliente_nome },
    { key: "localizacao", label: "Estado/Cidade", value: (p) => p.cliente_localizacao },
    { key: "criado_por", label: "Criado por", value: (p) => p.criado_por },
    { key: "departamento", label: "Departamento", value: (p) => p.departamento_label },
    { key: "paineis", label: "Painéis", value: (p) => p.paineis_info },
    { key: "valor_projeto", label: "Valor do Projeto", value: (p) => p.valor_projeto_info },
    { key: "valor_seguro", label: "Valor do Seguro", value: (p) => p.valor_seguro },
    { key: "orcamento", label: "Orçamento", value: (p) => (p.orcamento_id ? String(p.orcamento_id) : "-") },
    { key: "criado_em", label: "Criado em", value: (p) => p.criado_em },
  ];
  const [visibleProjetoCols, setVisibleProjetoCols] = useState<string[]>(projetoColumns.map((c) => c.key));
  const toggleProjetoCol = (key: string, nextVisible: boolean) => {
    setVisibleProjetoCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  // Log para debug
  console.log('🔍 Projetos carregados:', projetos);
  console.log('🔍 Quantidade de projetos:', projetos?.length);

  const getDepartamentoColor = (departamento: string) => {
    switch (departamento) {
      case 'vendas': return 'bg-blue-100 text-blue-800';
      case 'financeiro': return 'bg-green-100 text-green-800';
      case 'tecnico': return 'bg-orange-100 text-orange-800';
      case 'administrativo': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDepartamentoIcon = (departamento: string) => {
    switch (departamento) {
      case 'vendas': return <User className="h-4 w-4" />;
      case 'financeiro': return <DollarSign className="h-4 w-4" />;
      case 'tecnico': return <Wrench className="h-4 w-4" />;
      case 'administrativo': return <Settings className="h-4 w-4" />;
      default: return <FolderOpen className="h-4 w-4" />;
    }
  };

  const getDepartamentoLabel = (departamento: string) => {
    switch (departamento) {
      case 'vendas': return 'Vendas';
      case 'financeiro': return 'Financeiro';
      case 'tecnico': return 'Técnico';
      case 'administrativo': return 'Administrativo';
      default: return 'Vendas';
    }
  };

  const handleGerarOrcamento = async (projeto: any) => {
    try {
      // Preferir total_paineis (só tipo "painel"); fallback: somar equipamentos tipo painel
      let quantidadePainels = Number(projeto.total_paineis);
      if (!Number.isFinite(quantidadePainels) || quantidadePainels < 0) {
        const { data: equipamentos } = await supabase
          .from('projeto_equipamentos')
          .select('quantidade, equipamento:equipamentos(tipo)')
          .eq('projeto_id', projeto.id);

        quantidadePainels =
          equipamentos
            ?.filter((eq: any) => String(eq.equipamento?.tipo ?? '').toLowerCase() === 'painel')
            .reduce((total: number, eq: any) => total + (eq.quantidade || 0), 0) || 0;
      }

      setProjetoParaOrcamento({
        ...projeto,
        quantidadePainels
      });
      setShowGerarOrcamento(true);
    } catch (error) {
      console.error('Erro ao buscar equipamentos do projeto:', error);
      setProjetoParaOrcamento({
        ...projeto,
        quantidadePainels: Number(projeto.total_paineis) || 0
      });
      setShowGerarOrcamento(true);
    }
  };

  const handleConfirmarPlano = async (planoData: any) => {
    if (!projetoParaOrcamento) return;

    const clienteId = projetoParaOrcamento.cliente_id ?? projetoParaOrcamento.cliente?.id;
    if (!clienteId) {
      toast.error('Cliente do projeto não encontrado. Edite o projeto e vincule um cliente.');
      return;
    }

    const valorProjeto = projetoParaOrcamento.valor_projeto;
    const valorTotal =
      valorProjeto && valorProjeto > 0 ? valorProjeto : planoData.valor_total;
    if (!valorTotal || valorTotal <= 0) {
      toast.error('Informe o valor do projeto ou selecione um plano com valor válido.');
      return;
    }

    const valorSeguro = Number(planoData.valor_total) || 0;
    const valorTabela =
      Number(planoData.valor_tabela) > 0 ? Number(planoData.valor_tabela) : valorSeguro;
    const descontoValor = Math.max(
      0,
      Number(planoData.desconto_valor) ||
        Math.round((valorTabela - valorSeguro) * 100) / 100
    );

    const observacoesUsuario = String(projetoParaOrcamento.observacoes || '').trim();
    const descontoTxt =
      descontoValor > 0
        ? ` Desconto comercial: R$ ${descontoValor.toFixed(2)} (tabela R$ ${valorTabela.toFixed(2)} → final R$ ${valorSeguro.toFixed(2)}).`
        : '';
    const dadosOrcamento = {
      cliente_id: clienteId,
      projeto_id: projetoParaOrcamento.id,
      valor_total: valorTotal,
      observacoes: observacoesUsuario ||
        `Orçamento gerado do projeto ${projetoParaOrcamento.id}. Plano: ${planoData.tipo_plano}, Vigência: ${planoData.vigencia_anos} ano${planoData.vigencia_anos > 1 ? 's' : ''}, Quantidade de painéis: ${planoData.quantidade_placas}.${descontoTxt}`,
      plano_id: planoData.tipo_plano,
      quantidade_placas: planoData.quantidade_placas,
      valor_seguro: valorSeguro,
      vigencia_anos: planoData.vigencia_anos,
      forma_pagamento: planoData.forma_pagamento_id || null,
      parcelas_cartao: planoData.parcelas_cartao ?? null,
      numero_parcelas: planoData.parcelas_cartao ?? null,
      valor_tabela: valorTabela,
      desconto_valor: descontoValor,
    };

    try {
      await criarOrcamento.mutateAsync(dadosOrcamento);

      // Não vincula orcamento_id no projeto aqui: só após aprovação (pode haver vários orçamentos).
      // O vínculo projeto_id já fica no próprio orçamento.

      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      toast.success('Orçamento criado com sucesso!');
      setShowGerarOrcamento(false);
      setProjetoParaOrcamento(null);
    } catch (error) {
      toast.error(
        `Erro ao criar orçamento: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      );
    }
  };

  // Verificar se o usuário é TI (Super Admin) — edição de equipamentos
  const isTiUser = () => {
    if (!currentUser || !user) return false;
    return currentUser.role === 'ti' || user.email === 'suporte.ti@sunpro.srv.br';
  };

  // Os projetos já vêm filtrados do backend
  const projetosFiltrados = projetos;
  
  // Calcular dados da paginação
  const totalItems = totalProjetos;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = projetosFiltrados; // Já vem paginado e filtrado do backend

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Carregando projetos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 mb-4">❌ Erro ao carregar projetos</div>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Projetos</h2>
          <p className="text-sm text-gray-600">Gerencie o fluxo de trabalho dos seus projetos</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowNovoProjeto(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="h-4 w-4 mr-2" />
            Novo Projeto
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-gray-900">
              Lista de Projetos ({totalProjetos})
            </CardTitle>
            <div className="flex gap-2">
              <ListagemAcoes
                title="Projetos"
                subtitle={`Ordenação: ${sort.field} ${sort.direction} • Página ${currentPage}/${totalPages}`}
                rows={currentItems.map((projeto: any) => {
                  const criado_por =
                    (projeto.vendedor_id || projeto.orcamento?.vendedor_id)
                      ? (userNamesMap?.[projeto.vendedor_id ?? ""] || userNamesMap?.[projeto.orcamento?.vendedor_id ?? ""] || "—")
                      : "—";
                  const cliente_nome = projeto.cliente?.nome || `Cliente ID: ${projeto.cliente_id}`;
                  const paineis_info = String(projeto.total_paineis ?? 0);
                  const valor_projeto_info = (() => {
                    if (projeto.valor_projeto && projeto.valor_projeto > 0) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.valor_projeto));
                    }
                    if (projeto.contrato?.valor) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.contrato.valor));
                    }
                    if (projeto.orcamento?.valor_total) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.orcamento.valor_total));
                    }
                    return "-";
                  })();
                  const valor_seguro =
                    projeto.orcamento?.status === "aprovado" &&
                    projeto.orcamento?.valor_seguro &&
                    projeto.orcamento.valor_seguro > 0
                      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.orcamento.valor_seguro))
                      : "-";
                  const uf = (projeto.cliente?.estado || "—").trim();
                  const city = (projeto.cliente?.cidade || "—").trim();
                  const cliente_localizacao = `${uf} / ${city}`;
                  const departamento_label = getDepartamentoLabel(projeto.departamento || "vendas");
                  const criado_em = projeto.created_at ? new Date(projeto.created_at).toLocaleDateString("pt-BR") : "-";
                  return {
                    ...projeto,
                    cliente_nome,
                    criado_por,
                    departamento_label,
                    paineis_info,
                    valor_projeto_info,
                    valor_seguro,
                    cliente_localizacao,
                    criado_em,
                  } as ProjetoRow;
                })}
                fetchAllRows={async () => {
                  const batch = 1000;
                  let from = 0;
                  const all: any[] = [];

                  const userNameById = (id?: string | null) => {
                    if (!id) return "—";
                    const u = (usuarios || []).find((x: any) => x.id === id);
                    return u?.nome || u?.email || "—";
                  };

                  // Base query
                  const clienteFilterPresentExp = !!(filtrosAplicados.estado || filtrosAplicados.cidade);
                  const clienteEmbedExp = `cliente:clientes${clienteFilterPresentExp ? "!inner" : ""}(id, nome, email, telefone, cpf_cnpj, cidade, estado)`;

                  let base = supabase
                    .from("projetos")
                    .select(
                      `
                        *,
                        ${clienteEmbedExp},
                        orcamento:orcamentos!orcamento_id(id, valor_total, status, observacoes, created_at, plano_id, quantidade_placas, valor_seguro, vigencia_anos, vendedor_id),
                        contrato:contratos!projeto_id(id, valor, status, data_inicio, data_fim, arquivo_pdf_url)
                      `
                    );

                  if (isVendedor) base = base.eq("vendedor_id", user!.id);

                  if (filtroDepartamentoHook && filtroDepartamentoHook !== "todos") {
                    base = base.eq("departamento", filtroDepartamentoHook);
                  }

                  if (filtrosAplicados.dataInicio) base = base.gte("created_at", `${filtrosAplicados.dataInicio}T00:00:00`);
                  if (filtrosAplicados.dataFim) base = base.lte("created_at", `${filtrosAplicados.dataFim}T23:59:59`);
                  if (filtrosAplicados.criadoPorId) base = base.eq("vendedor_id", filtrosAplicados.criadoPorId);
                  if (filtrosAplicados.estado) base = base.eq("cliente.estado", filtrosAplicados.estado.toUpperCase());
                  if (filtrosAplicados.cidade) base = base.eq("cliente.cidade", filtrosAplicados.cidade);

                  const toNum = (s: string) => {
                    const trimmed = String(s ?? "").trim();
                    if (trimmed === "") return undefined;
                    const n = Number(trimmed.replace(",", "."));
                    return Number.isFinite(n) ? n : undefined;
                  };
                  const paineisMin = toNum(filtrosAplicados.paineisMin);
                  const paineisMax = toNum(filtrosAplicados.paineisMax);
                  const valorProjetoMin = toNum(filtrosAplicados.valorProjetoMin);
                  const valorProjetoMax = toNum(filtrosAplicados.valorProjetoMax);
                  const valorSeguroMin = toNum(filtrosAplicados.valorSeguroMin);
                  const valorSeguroMax = toNum(filtrosAplicados.valorSeguroMax);

                  if (paineisMin != null) base = base.gte("total_paineis", paineisMin);
                  if (paineisMax != null) base = base.lte("total_paineis", paineisMax);
                  if (valorProjetoMin != null) base = base.gte("valor_projeto", valorProjetoMin);
                  if (valorProjetoMax != null) base = base.lte("valor_projeto", valorProjetoMax);
                  if (valorSeguroMin != null) base = base.gte("orcamento.valor_seguro", valorSeguroMin);
                  if (valorSeguroMax != null) base = base.lte("orcamento.valor_seguro", valorSeguroMax);

                  // Search (same behavior as hook)
                  if (buscaProjeto && buscaProjeto.trim()) {
                    const searchLower = buscaProjeto.trim();
                    const searchNumbersOnly = searchLower.replace(/\D/g, "");
                    const clientesBase = () => {
                      let q = supabase.from("clientes").select("id");
                      if (isVendedor) q = q.eq("vendedor_id", user!.id);
                      return q;
                    };

                    if (searchNumbersOnly.length > 0 && /^\d+$/.test(searchLower)) {
                      if (searchNumbersOnly.length <= 6) {
                        base = base.eq("id", parseInt(searchLower));
                      } else {
                        const { data: clientesEncontrados } = await clientesBase().ilike("cpf_cnpj", `%${searchNumbersOnly}%`);
                        const clienteIds = (clientesEncontrados || []).map((c: any) => c.id);
                        base = clienteIds.length > 0 ? base.in("cliente_id", clienteIds) : base.eq("id", -1);
                      }
                    } else {
                      const { data: clientesEncontrados } = await clientesBase().or(`nome.ilike.%${searchLower}%,cpf_cnpj.ilike.%${searchLower}%`);
                      const clienteIds = (clientesEncontrados || []).map((c: any) => c.id);
                      base = clienteIds.length > 0 ? base.in("cliente_id", clienteIds) : base.eq("id", -1);
                    }
                  }

                  const sortField = sort?.field || "created_at";
                  const sortAsc = (sort?.direction || "desc") === "asc";

                  while (true) {
                    let q = base;
                    if (sortField === "cliente_nome") {
                      q = q.order("nome", { ascending: sortAsc, foreignTable: "cliente" });
                    } else if (sortField === "valor_seguro") {
                      q = q.order("valor_seguro", { ascending: sortAsc, foreignTable: "orcamento" });
                    } else {
                      q = q.order(sortField, { ascending: sortAsc });
                    }
                    q = q.range(from, from + batch - 1);

                    const { data, error } = await q;
                    if (error) throw error;
                    const rows = data || [];
                    all.push(...rows);
                    if (rows.length < batch) break;
                    from += batch;
                  }

                  return all.map((projeto: any) => {
                    const criado_por = userNameById(projeto.vendedor_id ?? projeto.orcamento?.vendedor_id);
                    const cliente_nome = projeto.cliente?.nome || `Cliente ID: ${projeto.cliente_id}`;
                    const paineis_info = String(projeto.total_paineis ?? 0);
                    const valor_projeto_info = (() => {
                      if (projeto.valor_projeto && projeto.valor_projeto > 0) {
                        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.valor_projeto));
                      }
                      if (projeto.contrato?.valor) {
                        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.contrato.valor));
                      }
                      if (projeto.orcamento?.valor_total) {
                        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.orcamento.valor_total));
                      }
                      return "-";
                    })();
                    const valor_seguro =
                      projeto.orcamento?.status === "aprovado" &&
                      projeto.orcamento?.valor_seguro &&
                      projeto.orcamento.valor_seguro > 0
                        ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(projeto.orcamento.valor_seguro))
                        : "-";
                    const departamento_label = getDepartamentoLabel(projeto.departamento || "vendas");
                    const criado_em = projeto.created_at ? new Date(projeto.created_at).toLocaleDateString("pt-BR") : "-";
                    return {
                      ...projeto,
                      cliente_nome,
                      criado_por,
                      departamento_label,
                      paineis_info,
                      valor_projeto_info,
                      valor_seguro,
                      criado_em,
                    } as ProjetoRow;
                  });
                }}
                columns={projetoColumns}
                visibleKeys={visibleProjetoCols}
                onToggleColumn={toggleProjetoCol}
                filenameBase={`projetos_filtrados`}
              />
            </div>
          </div>

          <div className="mt-4 bg-[#C9C9C9] rounded-lg p-4 text-xs">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-4">
                <Label>Buscar</Label>
                <Input
                  placeholder="Buscar por cliente, ID ou CPF..."
                  value={buscaProjetoInput}
                  onChange={(e) => setBuscaProjetoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setBuscaProjeto(buscaProjetoInput);
                    }
                  }}
                  className="h-8 text-xs"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Período - início</Label>
                <Input type="date" value={filtrosDraft.dataInicio} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataInicio: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label>Período - fim</Label>
                <Input type="date" value={filtrosDraft.dataFim} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataFim: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label>Departamento</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.departamento}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, departamento: e.target.value.toLowerCase() })}
                >
                  <option value="todos">Todos</option>
                  <option value="vendas">Vendas</option>
                  <option value="tecnico">Técnico</option>
                  <option value="financeiro">Financeiro</option>
                  <option value="administrativo">Administrativo</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <Label>Criado por</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.criadoPorId}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, criadoPorId: e.target.value })}
                >
                  <option value="">Todos</option>
                  {(usuarios || [])
                    .filter((u: any) => ["vendedor","vendedor_interno"].includes(u?.user_role || u?.role))
                    .map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.nome || u.email}
                      </option>
                    ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <Label>Estado</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.estado}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, estado: e.target.value, cidade: "" })}
                >
                  <option value="">Todos</option>
                  {BRASIL_UFS.map((uf) => (
                    <option key={uf.sigla} value={uf.sigla}>
                      {uf.sigla} - {uf.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <Label>Cidade</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs disabled:opacity-60"
                  value={filtrosDraft.cidade}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, cidade: e.target.value })}
                  disabled={!filtrosDraft.estado}
                >
                  <option value="">{filtrosDraft.estado ? "Todas" : "Selecione um estado"}</option>
                  {(cidadesDoEstado || []).map((c: string) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-4">
                <Label>Painéis (mín - máx)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" inputMode="numeric" step="1" placeholder="Min" value={filtrosDraft.paineisMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMin: e.target.value })} className="h-8 text-xs" />
                  <Input type="number" inputMode="numeric" step="1" placeholder="Máx" value={filtrosDraft.paineisMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMax: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>

              <div className="md:col-span-4">
                <Label>Valor do Projeto (mín - máx)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" inputMode="decimal" step="0.01" placeholder="Min" value={filtrosDraft.valorProjetoMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMin: e.target.value })} className="h-8 text-xs" />
                  <Input type="number" inputMode="decimal" step="0.01" placeholder="Máx" value={filtrosDraft.valorProjetoMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMax: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>

              <div className="md:col-span-4">
                <Label>Valor do Seguro (mín - máx)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" inputMode="decimal" step="0.01" placeholder="Min" value={filtrosDraft.valorSeguroMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMin: e.target.value })} className="h-8 text-xs" />
                  <Input type="number" inputMode="decimal" step="0.01" placeholder="Máx" value={filtrosDraft.valorSeguroMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMax: e.target.value })} className="h-8 text-xs" />
                </div>
              </div>

              <div className="md:col-span-12 flex items-end justify-end gap-2">
                <Button variant="outline" type="button" onClick={handleLimparFiltros} className="h-8 px-3 text-xs">
                  Limpar
                </Button>
                <Button type="button" className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3 text-xs" onClick={handleAplicarFiltros}>
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-[11px] leading-tight">
              <thead>
                <tr className="border-b">
                  {visibleProjetoCols.includes("id") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")}
                      onClick={() => toggleSort("id")}
                      title="Ordenar"
                    >
                      ID {sort.field === "id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("cliente") && (
                    <th className={cn(listagemThLeft, "cursor-pointer select-none")} onClick={() => toggleSort("cliente_nome")} title="Ordenar">
                      Cliente {sort.field === "cliente_nome" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("localizacao") && (
                    <th className={cn(listagemThLeft, "whitespace-nowrap hidden md:table-cell")}>
                      Estado/Cidade
                    </th>
                  )}
                  {visibleProjetoCols.includes("criado_por") && (
                    <th className={cn(listagemThLeft, "hidden lg:table-cell")}>Criado por</th>
                  )}
                  {visibleProjetoCols.includes("departamento") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap")}
                      onClick={() => toggleSort("departamento")}
                      title="Ordenar"
                    >
                      Departamento {sort.field === "departamento" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("paineis") && (
                    <th className={cn(listagemThCenter, "whitespace-nowrap hidden md:table-cell")}>Painéis</th>
                  )}
                  {visibleProjetoCols.includes("valor_projeto") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden lg:table-cell")}
                      onClick={() => toggleSort("valor_projeto")}
                      title="Ordenar"
                    >
                      Valor do Projeto {sort.field === "valor_projeto" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("valor_seguro") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden lg:table-cell")} onClick={() => toggleSort("valor_seguro")} title="Ordenar">
                      Valor do Seguro {sort.field === "valor_seguro" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("orcamento") && (
                    <th className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")} onClick={() => toggleSort("orcamento_id")} title="Ordenar">
                      Orçamento {sort.field === "orcamento_id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visibleProjetoCols.includes("criado_em") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell")}
                      onClick={() => toggleSort("created_at")}
                      title="Ordenar"
                    >
                      Criado em {sort.field === "created_at" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  <th className={cn(listagemThCenter, "whitespace-nowrap")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((projeto) => (
                  <tr key={projeto.id} className="border-b odd:bg-gray-50/60 hover:bg-gray-50">
                    {visibleProjetoCols.includes("id") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-mono text-[11px] whitespace-nowrap")}>
                        {projeto.id}
                      </td>
                    )}
                    {visibleProjetoCols.includes("cliente") && (
                      <td className={cn(listagemTdLeft, "text-gray-900")}>
                        <div className="max-w-[260px] truncate font-medium">{projeto.cliente?.nome || `Cliente ID: ${projeto.cliente_id}`}</div>
                        <div className="text-[10px] text-gray-500 md:hidden">
                          {(projeto.cliente?.estado || "—").trim()} / {(projeto.cliente?.cidade || "—").trim()}
                        </div>
                      </td>
                    )}
                    {visibleProjetoCols.includes("localizacao") && (
                      <td className={cn(listagemTdLeft, "text-gray-600 whitespace-nowrap hidden md:table-cell")}>
                        {projeto.cliente?.estado || "—"} / {projeto.cliente?.cidade || "—"}
                      </td>
                    )}
                    {visibleProjetoCols.includes("criado_por") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden lg:table-cell")}>
                      {(projeto.vendedor_id || projeto.orcamento?.vendedor_id)
                        ? (userNamesMap?.[projeto.vendedor_id ?? ""] || userNamesMap?.[projeto.orcamento?.vendedor_id ?? ""] || "—")
                        : "—"}
                      </td>
                    )}
                    {visibleProjetoCols.includes("departamento") && (
                      <td className={listagemTdCenter}>
                      <div className="flex justify-center">
                      <Badge className={getDepartamentoColor(projeto.departamento || 'vendas')}>
                        <div className="flex items-center gap-1">
                          {getDepartamentoIcon(projeto.departamento || 'vendas')}
                          {getDepartamentoLabel(projeto.departamento || 'vendas')}
                        </div>
                      </Badge>
                      </div>
                      </td>
                    )}
                    {visibleProjetoCols.includes("paineis") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden md:table-cell whitespace-nowrap")}>
                        {projeto.total_paineis ?? 0}
                      </td>
                    )}
                    {visibleProjetoCols.includes("valor_projeto") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap")}>
                      {(() => {
                        // Valor do Projeto: Prioridade: Valor do Projeto > Contrato > Orçamento
                        if (projeto.valor_projeto && projeto.valor_projeto > 0) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(projeto.valor_projeto));
                        }
                        if (projeto.contrato?.valor) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(projeto.contrato.valor));
                        }
                        if (projeto.orcamento?.valor_total) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(projeto.orcamento.valor_total));
                        }
                        return '-';
                      })()}
                      </td>
                    )}
                    {visibleProjetoCols.includes("valor_seguro") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap")}>
                      {(() => {
                        // Só exibe após orçamento aprovado (pode haver vários pendentes no mesmo projeto)
                        if (
                          projeto.orcamento?.status === "aprovado" &&
                          projeto.orcamento?.valor_seguro &&
                          projeto.orcamento.valor_seguro > 0
                        ) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(projeto.orcamento.valor_seguro));
                        }
                        return '-';
                      })()}
                      </td>
                    )}
                    {visibleProjetoCols.includes("orcamento") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap")}>
                      {projeto.orcamento_id ? (
                        <span className="font-mono text-sm">{projeto.orcamento_id}</span>
                      ) : (
                        '-'
                      )}
                      </td>
                    )}
                    {visibleProjetoCols.includes("criado_em") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap")}>
                      <div className="flex items-center justify-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(projeto.created_at).toLocaleDateString('pt-BR')}
                      </div>
                      </td>
                    )}
                    <td className={listagemTdCenter}>
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setProjetoSelecionado(projeto.id)}
                          className="p-2"
                          title="Ver Detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleGerarOrcamento(projeto)}
                          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 p-2"
                          title="Gerar Orçamento"
                        >
                          <Calculator className="h-4 w-4" />
                        </Button>
                        {isTiUser() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setProjetoParaEditar(projeto)}
                            className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 p-2"
                            title="Editar Projeto"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        )}
                        {isTiUser() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setProjetoEquipamentos(projeto)}
                            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 p-2"
                            title="Editar Equipamentos"
                          >
                            <Wrench className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete() && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setProjetoParaExcluir(projeto.id);
                              setShowConfirmDelete(true);
                            }}
                            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 p-2"
                            title="Excluir Projeto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {currentItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {projetos && projetos.length > 0 ? 
                  "Nenhum projeto encontrado com os filtros aplicados" : 
                  "Nenhum projeto encontrado"
                }
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

                <Dialog open={!!projetoSelecionado} onOpenChange={() => setProjetoSelecionado(null)}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Detalhes do Projeto {projetoSelecionado}</DialogTitle>
              </DialogHeader>
              
              {projetoSelecionado && (
                <ProjetoDetalhes
                  projetoId={projetoSelecionado}
                  onProjetoExcluido={() => setProjetoSelecionado(null)}
                />
              )}
            </DialogContent>
          </Dialog>

      <Dialog open={showNovoProjeto} onOpenChange={setShowNovoProjeto}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Projeto</DialogTitle>
          </DialogHeader>
          
          {showNovoProjeto && (
            <NovoProjeto onSuccess={() => setShowNovoProjeto(false)} />
          )}
        </DialogContent>
      </Dialog>

      <SelecionarPlanoDialog
        open={showGerarOrcamento}
        onOpenChange={setShowGerarOrcamento}
        onConfirm={handleConfirmarPlano}
        valorBase={projetoParaOrcamento?.valor_projeto || 0}
        quantidadePainels={projetoParaOrcamento?.quantidadePainels || 0}
      />

      {/* Dialog de edição */}
      {projetoParaEditar && (
        <EditarProjetoDialog
          projeto={projetoParaEditar}
          open={!!projetoParaEditar}
          onOpenChange={(open) => {
            if (!open) {
              setProjetoParaEditar(null);
            }
          }}
        />
      )}

      {projetoEquipamentos && (
        <EditarProjetoEquipamentosDialog
          projetoId={projetoEquipamentos.id}
          open={!!projetoEquipamentos}
          onOpenChange={(open) => {
            if (!open) {
              setProjetoEquipamentos(null);
            }
          }}
        />
      )}

      <ExcluirProjetoDialog
        projetoId={projetoParaExcluir}
        open={showConfirmDelete}
        onOpenChange={(open) => {
          setShowConfirmDelete(open);
          if (!open) setProjetoParaExcluir(null);
        }}
      />
    </div>
  );
}
